/**
 * Configuration for editable device parameters
 * Only operational parameters can be edited by users
 * Network/system parameters are read-only
 */

export interface EditableParameter {
  key: string;
  label: string;
  category: 'tracking' | 'motion' | 'environment' | 'safety';
  type: 'number' | 'boolean' | 'select';
  unit?: string;
  min?: number;
  max?: number;
  options?: { value: string | number; label: string }[];
  f0xxKey?: string; // F0XX protocol key if applicable
  description?: string;
  convertToMinutes?: boolean; // For displaying seconds as minutes
}

export const EDITABLE_PARAMETERS: EditableParameter[] = [
  // Tracking Intervals - User can adjust reporting frequencies
  // Note: AOVX protocol doesn't specify min/max, but we set reasonable defaults
  // Most devices work well with 10 minutes minimum to conserve battery
  {
    key: 'reporting_interval',
    label: 'Normal Report Interval', 
    category: 'tracking',
    type: 'number',
    unit: 'minutes',
    min: 10,      // 10 minutes minimum
    max: 1440,    // 24 hours maximum
    f0xxKey: 'F02E',
    description: 'How often the device reports location data to the server',
    convertToMinutes: true
  },
  {
    key: 'movement_report_interval',
    label: 'Movement Interval',
    category: 'tracking',
    type: 'number',
    unit: 'minutes',
    min: 10,      // 10 minutes minimum
    max: 1440,    // 24 hours maximum
    f0xxKey: 'F01E',
    description: 'How often the device reports when movement is detected',
    convertToMinutes: true
  },

  // Motion Detection Settings
  {
    key: 'gsensor_enable',
    label: 'Motion Detection',
    category: 'motion',
    type: 'boolean',
    f0xxKey: 'F019',
    description: 'Enable or disable motion detection'
  },
  {
    key: 'gsensor_threshold',
    label: 'Motion Sensitivity',
    category: 'motion',
    type: 'number',
    min: 10,
    max: 200,
    f0xxKey: 'F01A',
    description: 'Sensitivity threshold for motion detection (lower = more sensitive)'
  },
  {
    key: 'gsensor_count',
    label: 'Events to Trigger',
    category: 'motion',
    type: 'number',
    unit: 'events',
    min: 1,
    max: 10,
    f0xxKey: 'F01C',
    description: 'Number of motion events required to trigger an alert'
  },
  {
    key: 'gsensor_time_window',
    label: 'Time Window',
    category: 'motion',
    type: 'number',
    unit: 'seconds',
    min: 5,
    max: 300,
    f0xxKey: 'F01D',
    description: 'Time window for counting motion events'
  },
  {
    key: 'trigger_mode_interval',
    label: 'Trigger Mode Interval',
    category: 'motion',
    type: 'number',
    unit: 'minutes',
    min: 10,      // 10 minutes minimum
    max: 1440,    // 24 hours maximum
    f0xxKey: 'F02F',
    description: 'Reporting interval when device is in triggered state',
    convertToMinutes: true
  },

  // Environmental Monitoring
  {
    key: 'sensor_alarm_enabled',
    label: 'Temperature/Humidity Monitoring',
    category: 'environment',
    type: 'boolean',
    description: 'Enable temperature and humidity monitoring'
  },
  {
    key: 'temp_threshold_low',
    label: 'Temperature Min',
    category: 'environment',
    type: 'number',
    unit: '°C',
    min: -40,
    max: 85,
    description: 'Minimum temperature threshold'
  },
  {
    key: 'temp_threshold_high',
    label: 'Temperature Max',
    category: 'environment',
    type: 'number',
    unit: '°C',
    min: -40,
    max: 85,
    description: 'Maximum temperature threshold'
  },
  {
    key: 'humidity_threshold_low',
    label: 'Humidity Min',
    category: 'environment',
    type: 'number',
    unit: '%',
    min: 0,
    max: 100,
    description: 'Minimum humidity threshold'
  },
  {
    key: 'humidity_threshold_high',
    label: 'Humidity Max',
    category: 'environment',
    type: 'number',
    unit: '%',
    min: 0,
    max: 100,
    description: 'Maximum humidity threshold'
  },

  // Safety Alerts
  {
    key: 'max_speed_threshold',
    label: 'Speed Limit',
    category: 'safety',
    type: 'number',
    unit: 'km/h',
    min: 10,
    max: 300,
    description: 'Maximum speed before triggering an alert'
  },
  {
    key: 'low_battery_threshold',
    label: 'Low Battery Alert',
    category: 'safety',
    type: 'number',
    unit: '%',
    min: 5,
    max: 50,
    description: 'Battery level to trigger low battery alert'
  }
];

// Parameters that are READ-ONLY (cannot be edited by users)
export const READ_ONLY_PARAMETERS = [
  'main_server',
  'main_server_port',
  'ntp_server',
  'ntp_port',
  'timezone',
  'device_id',
  'device_id_config',
  'firmware_version',
  'hardware_version',
  'iccid',
  'gsensor_range' // This is hardware-specific
];

// Helper function to check if a parameter is editable
export function isParameterEditable(paramKey: string): boolean {
  return EDITABLE_PARAMETERS.some(p => p.key === paramKey);
}

// Helper function to get parameter config
export function getParameterConfig(paramKey: string): EditableParameter | undefined {
  return EDITABLE_PARAMETERS.find(p => p.key === paramKey);
}

// Validate a parameter value
export function validateParameterValue(paramKey: string, value: any): { valid: boolean; error?: string } {
  const config = getParameterConfig(paramKey);
  if (!config) {
    return { valid: false, error: 'Parameter not editable' };
  }

  if (config.type === 'number') {
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return { valid: false, error: 'Must be a number' };
    }
    
    // For minutes-based parameters, validate against the original minutes range
    let minLimit = config.min;
    let maxLimit = config.max;
    let compareValue = numValue;
    
    // If the value is already in seconds (from save), convert limits to seconds
    if (config.convertToMinutes && maxLimit !== undefined && numValue > maxLimit) {
      minLimit = (config.min || 0) * 60;
      maxLimit = (config.max || 999999) * 60;
    }
    
    if (minLimit !== undefined && compareValue < minLimit) {
      const displayMin = config.convertToMinutes ? config.min : minLimit;
      return { valid: false, error: `Must be at least ${displayMin} ${config.unit}` };
    }
    if (maxLimit !== undefined && compareValue > maxLimit) {
      const displayMax = config.convertToMinutes ? config.max : maxLimit;
      return { valid: false, error: `Must be at most ${displayMax} ${config.unit}` };
    }
  }

  if (config.type === 'boolean') {
    if (typeof value !== 'boolean' && value !== 0 && value !== 1 && value !== '0' && value !== '1') {
      return { valid: false, error: 'Must be true or false' };
    }
  }

  return { valid: true };
}